import { useCallback, useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { useViewer, useViewerStoreHandle } from '../store/context';
import { clearMarks, CURRENT_CLASS, highlight } from './find-dom';

/** Find-in-document over the rendered DOM. Returns navigation callbacks. */
export function useFind(containerRef: RefObject<HTMLElement | null>) {
  const query = useViewer((s) => s.findQuery);
  const findOpen = useViewer((s) => s.findOpen);
  // React to every render (open + live preview), not just new documents, so
  // highlights re-scan the current DOM while editing.
  const renderVersion = useViewer((s) => s.renderVersion);
  const store = useViewerStoreHandle();

  const marksRef = useRef<HTMLElement[]>([]);
  const currentRef = useRef(0);

  const applyCurrent = useCallback(
    (idx: number) => {
      const marks = marksRef.current;
      marks.forEach((m, i) => m.classList.toggle(CURRENT_CLASS, i === idx));
      currentRef.current = idx;
      // Publish the count/index BEFORE scrolling: scrollIntoView is a no-op (or
      // can throw) on some engines/contexts, and it must never block the status
      // update — otherwise the bar would appear stuck at a stale count.
      store.getState().setFindStatus(marks.length, marks.length ? idx + 1 : 0);
      marks[idx]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    },
    [store],
  );

  useEffect(() => {
    // Search ONLY the rendered document, never the surrounding chrome (find bar,
    // prompts). That keeps highlighting from mutating React-controlled DOM and
    // makes the match count reflect the document alone.
    const root = containerRef.current?.querySelector<HTMLElement>('.markroom-doc') ?? null;
    clearMarks(root);
    marksRef.current = [];
    currentRef.current = 0;

    const trimmed = query.trim();
    if (!root || !findOpen || trimmed === '') {
      store.getState().setFindStatus(0, 0);
      return;
    }
    const marks = highlight(root, trimmed);
    marksRef.current = marks;
    if (marks.length > 0) {
      applyCurrent(0);
    } else {
      store.getState().setFindStatus(0, 0);
    }
    return () => clearMarks(root);
  }, [query, findOpen, renderVersion, containerRef, store, applyCurrent]);

  const next = useCallback(() => {
    const n = marksRef.current.length;
    if (n === 0) return;
    applyCurrent((currentRef.current + 1) % n);
  }, [applyCurrent]);

  const prev = useCallback(() => {
    const n = marksRef.current.length;
    if (n === 0) return;
    applyCurrent((currentRef.current - 1 + n) % n);
  }, [applyCurrent]);

  return { next, prev };
}
